import { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  TableCell,
  Typography,
  Button,
  InputBase,
  Drawer,
  Divider,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { format, startOfDay, subDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import Iconify from '@/components/iconify';
import SearchableTable from '@/components/table/SearchableTable';
import { ProductRatingsContext } from '@/contexts/ProductRatingsContext';

const labels = [
  { id: 'created_at', label: 'Data' },
  { id: 'buyer_name', label: 'Nome' },
  { id: 'buyer_phone', label: 'Número' },
  { id: 'score', label: 'Nota' },
  { id: 'comment', label: 'Descrição' },
  { id: 'status', label: 'Status' },
];

const statusLabel = {
  active: 'Ativo',
  paid: 'Ativo',
  canceled: 'Cancelado',
  refunded: 'Reembolsado',
};

const statusStyles = {
  active: {
    bgcolor: 'rgba(15, 120, 100, 0.2)',
    color: '#36B37E',
    borderColor: 'transparent',
  },
  paid: {
    bgcolor: 'rgba(15, 120, 100, 0.2)',
    color: '#36B37E',
    borderColor: 'transparent',
  },
  canceled: {
    bgcolor: 'rgba(255, 86, 48, 0.2)',
    color: '#FF5630',
    borderColor: 'transparent',
  },
  refunded: {
    bgcolor: 'rgba(253, 212, 101, 0.2)',
    color: '#FDD465',
    borderColor: 'transparent',
  },
};

function onlyDigits(value = '') {
  return (value || '').replace(/\D/g, '');
}

function getScoreColor(score) {
  if (score <= 1) return '#D05E44';
  if (score <= 2) return '#DBB449';
  if (score <= 3) return '#0F7864';
  if (score <= 4) return '#1D845B';
  return '#35B37D';
}

const datePresetLabel = {
  always: 'Sempre',
  today: 'Hoje',
  yesterday: 'Ontem',
  last7: 'Últimos 7 dias',
  last30: 'Últimos 30 dias',
  custom: 'Outro',
};

// === ÍCONE DO CACTO (SVG) ===
function CactusIcon({ color = '#D05E44' }) {
  return (
    <svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.1">
        <path
          d="M4.72574 15.6192C4.18656 15.6192 3.59105 15.5226 3.10015 15.6192C2.45635 15.7157 1.76427 16.3595 2.56097 16.9067C2.9553 17.1562 4.0417 17.0033 4.48431 17.0516C5.07982 17.0999 5.619 17.1965 6.21452 17.1965C6.81003 17.1965 7.69525 17.293 8.24248 17.0516C8.98284 16.7538 9.18403 16.0135 8.33905 15.6192C7.45382 15.2248 5.91676 15.8686 4.88669 15.7157"
          fill="black"
        />
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.60463 9.90569C2.18023 9.90569 0.71559 9.81717 0.474166 8.1433C0.321265 7.10517 -0.0408708 4.57022 1.35939 4.23223C3.04935 3.82986 2.78379 5.68077 2.85621 6.7189C2.9045 7.38683 3.62877 7.6524 3.57244 6.83156C3.52415 6.15557 3.44368 5.47959 3.44368 4.8036C3.44368 3.41139 3.3793 1.71338 4.47375 0.659159C5.46359 -0.290441 7.04089 -0.258251 7.70883 1.04544C8.40091 2.38132 8.11925 4.05519 8.10316 5.48763C8.08706 7.08103 7.98244 8.66638 7.98244 10.2517C9.35856 10.0747 8.94814 8.78709 8.98033 7.70069C9.01252 6.74304 9.78507 5.20597 10.8795 6.35676C11.7084 7.21784 11.4348 9.3987 11.2497 10.461C11.0002 11.9578 10.0828 12.4809 8.63429 12.5211C7.57202 12.5533 7.99049 14.4123 8.00659 15.1768C8.02268 15.8447 7.9583 16.0459 7.32255 16.3356C6.71899 16.6092 5.96253 16.7058 5.30264 16.6253C4.8037 16.5609 4.28866 16.3597 3.82191 16.1827C3.50806 16.0539 3.67705 14.5088 3.66901 14.1628C3.66096 12.7867 3.8541 11.2657 3.58853 9.91374L3.60463 9.90569Z"
        fill={color}
      />
      <path
        opacity="0.1"
        d="M2.84713 6.34015C2.83104 5.54345 2.84713 4.53752 2.20334 4.25586C2.10677 5.00427 2.02629 6.40453 2.84713 6.34015Z"
        fill="white"
      />
      <path
        opacity="0.1"
        d="M7.02509 5.87353C7.009 6.83922 6.96071 8.1912 8.02297 8.44067C8.05516 7.45888 8.0954 6.46904 8.1115 5.4792C8.12759 4.03871 8.40121 2.36484 7.71717 1.03701C7.55622 0.723155 7.33089 0.481732 7.08142 0.304688C7.08142 2.1556 7.06533 4.01456 7.02509 5.86548V5.87353Z"
        fill="white"
      />
    </svg>
  );
}

CactusIcon.propTypes = {
  color: PropTypes.string,
};

// --- helpers de filtro/CSV ---

const defaultFilters = {
  datePreset: 'always', // always | today | yesterday | last7 | last30 | custom
  startDate: '',
  endDate: '',
  statuses: {
    active: false,
    refunded: false,
    canceled: false,
  },
  scores: {
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  },
};

function normalizeStatus(status) {
  if (status === 'paid') return 'active';
  return status;
}

function applyFilters(rows, filters) {
  const { statuses, scores, datePreset, startDate, endDate } = filters;

  const selectedStatuses = Object.keys(statuses).filter((k) => statuses[k]);
  const selectedScores = Object.keys(scores).filter((k) => scores[k]);

  const today = new Date();
  const todayStart = startOfDay(today);

  return (rows || []).filter((row) => {
    const {
      status,
      sale_status: saleStatus,
      score,
      rating,
      created_at: createdAtApi,
      date,
      createdAt,
    } = row;

    const statusRaw = status || saleStatus;
    const statusNorm = normalizeStatus(statusRaw || '');

    // status
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(statusNorm)) {
      return false;
    }

    // score
    const scoreNum = Number(score || rating || 0);
    if (selectedScores.length > 0 && !selectedScores.includes(String(scoreNum))) {
      return false;
    }

    // date
    const dateValue = createdAtApi || date || createdAt;
    if (!dateValue) {
      return false;
    }
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return false;

    const dStart = startOfDay(d);

    switch (datePreset) {
      case 'today': {
        return dStart.getTime() === todayStart.getTime();
      }
      case 'yesterday': {
        const yStart = subDays(todayStart, 1);
        return dStart.getTime() === yStart.getTime();
      }
      case 'last7': {
        const from = subDays(todayStart, 6);
        return dStart.getTime() >= from.getTime() && dStart.getTime() <= todayStart.getTime();
      }
      case 'last30': {
        const from = subDays(todayStart, 29);
        return dStart.getTime() >= from.getTime() && dStart.getTime() <= todayStart.getTime();
      }
      case 'custom': {
        if (!startDate || !endDate) return true;
        const from = startOfDay(new Date(startDate));
        const to = startOfDay(new Date(endDate));
        return dStart.getTime() >= from.getTime() && dStart.getTime() <= to.getTime();
      }
      case 'always':
      default:
        return true;
    }
  });
}

function escapeCsvValue(value) {
  const str = String(value ?? '');
  if (str.includes(';') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(rows) {
  const header = ['Data', 'Nome', 'Número', 'Nota', 'Descrição', 'Status'];
  const lines = [header.map(escapeCsvValue).join(';')];

  (rows || []).forEach((row) => {
    const {
      created_at: createdAtApi,
      date,
      createdAt,
      buyer_name: buyerName,
      name,
      buyer_phone: buyerPhone,
      phone,
      cellphone,
      score,
      rating,
      comment: commentRaw,
      description,
      status,
      sale_status: saleStatus,
    } = row;

    const dateValue = createdAtApi || date || createdAt;
    const formattedDate = dateValue
      ? format(new Date(dateValue), 'dd/MM/yyyy', { locale: ptBR })
      : '';

    const resolvedName = buyerName || name || '';
    const resolvedPhone = buyerPhone || phone || cellphone || '';
    const resolvedScore = score || rating || '';
    const comment = commentRaw || description || '';
    const statusResolved = statusLabel[status || saleStatus] || status || saleStatus || '';

    const values = [
      formattedDate,
      resolvedName,
      resolvedPhone,
      resolvedScore,
      comment,
      statusResolved,
    ];

    lines.push(values.map(escapeCsvValue).join(';'));
  });

  return lines.join('\n');
}

function triggerDownload(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function RatingsLoadError({ onRetry }) {
  return (
    <Stack width={1} alignItems="center" justifyContent="center" spacing={1.5} p={3}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Não foi possível carregar as avaliações.
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={onRetry}
        startIcon={<Iconify icon="eva:refresh-fill" />}
      >
        Tentar novamente
      </Button>
    </Stack>
  );
}

RatingsLoadError.propTypes = {
  onRetry: PropTypes.func.isRequired,
};

export default function ProductReviewsTable() {
  const { ratings, fetching, table, isError, refetch } = useContext(ProductRatingsContext);
  // Durante o retry a barra de carregamento basta; repetir a mensagem faria o clique parecer
  // ignorado. Com o `keepPreviousData`, `ratings` ainda traz a página anterior quando a nova
  // falha, e mostrá-la passaria linha velha por resultado do filtro atual.
  const showError = isError && !fetching;
  const rawRows = useMemo(() => (isError ? [] : ratings || []), [isError, ratings]);

  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersDraft, setFiltersDraft] = useState(defaultFilters);
  const [filtersApplied, setFiltersApplied] = useState(defaultFilters);
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const appliedRows = useMemo(
    () => applyFilters(rawRows, filtersApplied),
    [rawRows, filtersApplied]
  );

  const { rowsPerPage: rawRowsPerPage = 10, page: rawPageIndex = 0 } = table || {};
  const rowsPerPage = rawRowsPerPage || 10;
  const pageIndex = rawPageIndex || 0;
  const pageCount = Math.max(1, Math.ceil((appliedRows.length || 0) / (rowsPerPage || 10)));

  const previousPage = () => {
    if (!table) return;
    if (pageIndex > 0) {
      table.setPage(pageIndex - 1);
    }
  };

  const nextPage = () => {
    if (!table) return;
    if (pageIndex + 1 < pageCount) {
      table.setPage(pageIndex + 1);
    }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearch(value);
    if (table && typeof table.setGlobalFilter === 'function') {
      table.setGlobalFilter(value);
    }
  };

  const handleOpenFilters = () => {
    setFiltersDraft(filtersApplied);
    setFiltersOpen(true);
  };

  const handleCloseFilters = () => {
    setFiltersOpen(false);
  };

  const handleApplyFilters = () => {
    setFiltersApplied(filtersDraft);
    setFiltersOpen(false);
    if (table && typeof table.setPage === 'function') {
      table.setPage(0);
    }
  };

  const handleToggleStatus = (key) => {
    setFiltersDraft((prev) => {
      const { statuses } = prev;
      return {
        ...prev,
        statuses: {
          ...statuses,
          [key]: !statuses[key],
        },
      };
    });
  };

  const handleToggleScore = (score) => {
    setFiltersDraft((prev) => {
      const { scores } = prev;
      return {
        ...prev,
        scores: {
          ...scores,
          [score]: !scores[score],
        },
      };
    });
  };

  const handleDatePresetChange = (preset) => {
    setFiltersDraft((prev) => ({
      ...prev,
      datePreset: preset,
    }));
    setDateMenuAnchor(null);
  };

  const handleOpenExportMenu = (event) => {
    const { currentTarget } = event;
    setExportMenuAnchor(currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = (result) => {
    const csv = buildCsv(appliedRows);

    if (result === 'csv') {
      triggerDownload(csv, 'avaliacoes.csv', 'text/csv;charset=utf-8;');
    } else if (result === 'xls') {
      triggerDownload(csv, 'avaliacoes.xls', 'application/vnd.ms-excel');
    }

    handleCloseExportMenu();
  };

  return (
    <>
      <Box
        sx={{
          width: 1165,
          height: 597,
          mx: 'auto',
          mt: 2,
          borderRadius: '15.9589px',
          bgcolor: '#212B36',
          overflow: 'hidden',
          position: 'relative',

          '& .MuiTable-root': {
            tableLayout: 'fixed',
          },
          '& .MuiTableHead-root .MuiTableRow-root': {
            backgroundColor: '#333E49',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#919EAB',
            fontFamily: 'Public Sans',
            fontWeight: 700,
            fontSize: '13.964px',
            lineHeight: '20px',
            borderBottom: 'none',
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            color: '#FFFFFF',
            fontFamily: 'Public Sans',
            fontWeight: 400,
            fontSize: '13.964px',
            lineHeight: '20px',
            borderBottom: 'none',
          },
          '& .MuiTableCell-root': {
            paddingTop: '15.9589px',
            paddingBottom: '15.9589px',
            paddingLeft: 0,
            paddingRight: 0,
          },
          '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:first-of-type': {
            paddingLeft: '38px',
          },
          '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:first-of-type': {
            paddingLeft: '40px',
          },
          '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root + .MuiTableCell-root': {
            paddingLeft: '48px',
          },
          '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root + .MuiTableCell-root': {
            paddingLeft: '61px',
          },
          '& .MuiTableContainer-root': {
            pb: '8px',
          },
          '& .MuiTablePagination-root': {
            display: 'none',
          },
        }}
      >
        {/* HEADER – PESQUISAR / FILTROS / EXPORTAR */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '40px',
            pt: '24px',
            pb: '16px',
          }}
        >
          {/* Campo de busca */}
          <Box
            sx={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              px: '14px',
              width: 280,
              height: 44,
              border: '1px solid rgba(145, 158, 171, 0.4)',
              borderRadius: '8px',
              backgroundColor: 'transparent',
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 22, color: '#919EAB' }} />
            <InputBase
              placeholder="Pesquisar"
              value={search}
              onChange={handleSearchChange}
              sx={{
                flex: 1,
                height: 24,
                fontFamily: 'Public Sans',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#F4F6F8',
                '& input::placeholder': {
                  color: '#919EAB',
                  opacity: 1,
                },
              }}
            />
          </Box>

          {/* Botões Filtros / Exportar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '24px',
              height: 44,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<TuneRoundedIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />}
              onClick={handleOpenFilters}
              sx={{
                height: 44,
                px: '22px',
                borderRadius: '10px',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: '#FFFFFF',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'Public Sans',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                '&:hover': {
                  borderColor: '#FFFFFF',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              Filtros
            </Button>

            <Button
              variant="contained"
              endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />}
              onClick={handleOpenExportMenu}
              disabled={isError}
              sx={{
                height: 44,
                px: '24px',
                borderRadius: '10px',
                backgroundColor: '#0F7864',
                boxShadow: 'none',
                textTransform: 'none',
                fontFamily: 'Public Sans',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0D6A59',
                  boxShadow: 'none',
                },
              }}
            >
              Exportar
            </Button>
          </Box>
        </Box>

        {/* TABELA */}
        <SearchableTable
          labels={labels}
          rows={appliedRows}
          count={appliedRows.length}
          fetching={fetching}
          table={table}
          searchable={false}
          filterable={false}
          emptyStateContent={showError ? <RatingsLoadError onRetry={refetch} /> : null}
          sx={{ borderRadius: '0px' }}
          renderRow={(row) => {
            const {
              created_at: createdAtApi,
              date,
              createdAt,
              buyer_phone: buyerPhone,
              phone,
              cellphone,
              score,
              rating,
              comment: commentRaw,
              description,
              status,
              sale_status: saleStatus,
              buyer_name: buyerName,
              name,
            } = row;

            const dateValue = createdAtApi || date || createdAt;
            const phoneValue = buyerPhone || phone || cellphone;
            const scoreValue = Number(score || rating || 0);
            const commentValue = commentRaw || description || '';
            const statusValue = status || saleStatus;

            const digits = onlyDigits(phoneValue);
            const waLink = digits ? `https://wa.me/55${digits}` : null;

            const scoreColor = getScoreColor(scoreValue || 0);

            return (
              <>
                {/* DATA */}
                <TableCell>
                  {dateValue ? format(new Date(dateValue), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                </TableCell>

                {/* NOME */}
                <TableCell>
                  <Typography>{buyerName || name || '-'}</Typography>
                </TableCell>

                {/* NÚMERO */}
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      height: '20px',
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#1FAF38',
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 12, color: '#FFFFFF' }} />
                    </Box>

                    {waLink ? (
                      <MuiLink
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{
                          color: '#FFFFFF',
                          fontFamily: 'Public Sans',
                          fontSize: '13.964px',
                        }}
                      >
                        {phoneValue}
                      </MuiLink>
                    ) : (
                      <Typography>{phoneValue || '-'}</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* NOTA – cacto + número */}
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      height: '20px',
                      color: scoreColor,
                    }}
                  >
                    <CactusIcon color={scoreColor} />
                    <Typography sx={{ color: scoreColor }}>{scoreValue || '-'}</Typography>
                  </Box>
                </TableCell>

                {/* DESCRIÇÃO */}
                <TableCell sx={{ maxWidth: 190 }}>
                  <Typography noWrap title={commentValue}>
                    {commentValue || '-'}
                  </Typography>
                </TableCell>

                {/* STATUS */}
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel[statusValue] || statusValue || '-'}
                    sx={{
                      borderRadius: '5px',
                      fontFamily: 'Public Sans',
                      fontWeight: 700,
                      fontSize: '11.9692px',
                      lineHeight: '18px',
                      px: '7.97945px',
                      py: '3.98973px',
                      ...(statusStyles[statusValue] || {}),
                    }}
                  />
                </TableCell>
              </>
            );
          }}
        />

        {/* Paginação customizada */}
        {table && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%',
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              pb: '6px',
            }}
          >
            <IconButton
              size="small"
              onClick={previousPage}
              disabled={pageIndex === 0}
              sx={{ width: 20, height: 20 }}
            >
              <ChevronLeftRoundedIcon sx={{ fontSize: 20, color: '#919EAB' }} />
            </IconButton>

            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '32px',
                bgcolor: '#0F7864',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Public Sans',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: '24px',
                color: '#FFFFFF',
              }}
            >
              {pageIndex + 1}
            </Box>

            <IconButton
              size="small"
              onClick={nextPage}
              disabled={pageIndex + 1 >= pageCount}
              sx={{ width: 20, height: 20 }}
            >
              <ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#919EAB' }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* DRAWER DE FILTROS – agora ANCORA À DIREITA */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={handleCloseFilters}
        PaperProps={{
          sx: {
            width: 320,
            bgcolor: '#161C24',
            color: '#F4F6F8',
            borderRadius: '16px 0 0 16px',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleCloseFilters} sx={{ color: '#F4F6F8' }}>
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Typography
              sx={{
                fontFamily: 'Public Sans',
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              Filtros
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              height: 32,
              px: 2.5,
              borderRadius: '999px',
              backgroundColor: '#0F7864',
              textTransform: 'none',
              fontFamily: 'Public Sans',
              fontWeight: 700,
              fontSize: 14,
              '&:hover': {
                backgroundColor: '#0D6A59',
              },
            }}
          >
            Aplicar
          </Button>
        </Box>

        <Divider sx={{ borderColor: 'rgba(145, 158, 171, 0.24)' }} />

        <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Data de criação */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Public Sans',
                fontWeight: 600,
                fontSize: 14,
                color: '#919EAB',
              }}
            >
              Data de criação
            </Typography>

            <Button
              variant="outlined"
              onClick={(e) => {
                const { currentTarget } = e;
                setDateMenuAnchor(currentTarget);
              }}
              endIcon={<CalendarTodayRoundedIcon sx={{ fontSize: 16, color: '#919EAB' }} />}
              sx={{
                justifyContent: 'space-between',
                borderRadius: '8px',
                borderColor: 'rgba(145, 158, 171, 0.5)',
                color: '#F4F6F8',
                textTransform: 'none',
                fontFamily: 'Public Sans',
                fontSize: 14,
                px: 1.5,
              }}
            >
              {datePresetLabel[filtersDraft.datePreset] || 'Sempre'}
            </Button>

            {filtersDraft.datePreset === 'custom' && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Public Sans',
                    fontWeight: 600,
                    fontSize: 13,
                    color: '#919EAB',
                  }}
                >
                  Selecione o período
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      height: 40,
                      borderRadius: '8px',
                      border: '1px solid rgba(145,158,171,0.5)',
                      gap: 1,
                    }}
                  >
                    <InputBase
                      type="date"
                      value={filtersDraft.startDate}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFiltersDraft((prev) => ({
                          ...prev,
                          startDate: value,
                        }));
                      }}
                      sx={{
                        flex: 1,
                        fontFamily: 'Public Sans',
                        fontSize: 13,
                        color: '#F4F6F8',
                      }}
                    />
                    <CalendarTodayRoundedIcon sx={{ fontSize: 16, color: '#919EAB' }} />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      height: 40,
                      borderRadius: '8px',
                      border: '1px solid rgba(145,158,171,0.5)',
                      gap: 1,
                    }}
                  >
                    <InputBase
                      type="date"
                      value={filtersDraft.endDate}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFiltersDraft((prev) => ({
                          ...prev,
                          endDate: value,
                        }));
                      }}
                      sx={{
                        flex: 1,
                        fontFamily: 'Public Sans',
                        fontSize: 13,
                        color: '#F4F6F8',
                      }}
                    />
                    <CalendarTodayRoundedIcon sx={{ fontSize: 16, color: '#919EAB' }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Status */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Public Sans',
                fontWeight: 600,
                fontSize: 14,
                color: '#919EAB',
              }}
            >
              Status
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!filtersDraft.statuses.active}
                  onChange={() => handleToggleStatus('active')}
                  sx={{ color: '#0F7864' }}
                />
              }
              label="Ativo"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!filtersDraft.statuses.refunded}
                  onChange={() => handleToggleStatus('refunded')}
                  sx={{ color: '#FDD465' }}
                />
              }
              label="Reembolsado"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!filtersDraft.statuses.canceled}
                  onChange={() => handleToggleStatus('canceled')}
                  sx={{ color: '#FF5630' }}
                />
              }
              label="Cancelado"
            />
          </Box>

          {/* Nota */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Public Sans',
                fontWeight: 600,
                fontSize: 14,
                color: '#919EAB',
              }}
            >
              Nota
            </Typography>

            {[1, 2, 3, 4, 5].map((score) => (
              <FormControlLabel
                key={score}
                control={
                  <Checkbox
                    checked={!!filtersDraft.scores[score]}
                    onChange={() => handleToggleScore(score)}
                    sx={{ color: '#0F7864' }}
                  />
                }
                label={String(score)}
              />
            ))}
          </Box>
        </Box>
      </Drawer>

      {/* MENU de presets de data */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: '#161C24',
            color: '#F4F6F8',
            borderRadius: '12px',
            minWidth: 220,
          },
        }}
      >
        <MenuItem onClick={() => handleDatePresetChange('today')}>Hoje</MenuItem>
        <MenuItem onClick={() => handleDatePresetChange('yesterday')}>Ontem</MenuItem>
        <MenuItem onClick={() => handleDatePresetChange('last7')}>Últimos 7 dias</MenuItem>
        <MenuItem onClick={() => handleDatePresetChange('last30')}>Últimos 30 dias</MenuItem>
        <MenuItem onClick={() => handleDatePresetChange('always')}>Sempre</MenuItem>
        <MenuItem onClick={() => handleDatePresetChange('custom')}>Outro</MenuItem>
      </Menu>

      {/* MENU de exportação CSV / XLS */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleCloseExportMenu}
        PaperProps={{
          sx: {
            bgcolor: '#161C24',
            color: '#F4F6F8',
            borderRadius: '12px',
            minWidth: 160,
          },
        }}
      >
        <MenuItem onClick={() => handleExport('csv')}>CSV</MenuItem>
        <MenuItem onClick={() => handleExport('xls')}>XLS</MenuItem>
      </Menu>
    </>
  );
}
